import React from 'react';
import { Moment } from 'moment';

import RecurrenceSettingsInput from 'Dashboard/components/RecurrenceSettingsInput/RecurrenceSettingsInput';
import RecurrenceTimeInMonthInput from 'Dashboard/components/RecurrenceTimeInMonthInput/RecurrenceTimeInMonthInput';
import DayPicker from 'Shared/components/DayPicker/DayPicker';
import TimeInput from 'Shared/components/TimeInput/TimeInput';
import CustomSingleDatePicker from 'Shared/components/CustomSingleDatePicker/CustomSingleDatePicker';
import { NEW_TIME_OF_DAY_DEFAULT } from '../utils';

import './RevisitEditor.scss';

type Props = {
  days: string;
  setDay: Function;
  intervalUnit: string;
  setIntervalUnit: Function;
  intervalAmount: string | number;
  setIntervalAmount: Function;
  timeOfDay: string;
  setTimeOfDay: Function;
  hideRecurrenceLabel?: boolean;
  startsAt: Moment;
  setStartsAt: Function;
  hideTimeOfDayInput: boolean;
  dayInMonth: number;
  setDayInMonth: Function;
  weekInMonth: number;
  setWeekInMonth: Function;
};

const intervalUnitOptions = intervalAmount => [
  { value: 'day', label: `day${intervalAmount > 1 ? 's' : ''}` },
  { value: 'week', label: `week${intervalAmount > 1 ? 's' : ''}` },
  { value: 'month', label: `month${intervalAmount > 1 ? 's' : ''}` },
];

const formatRecurrenceInput = value => {
  return value ? parseInt(value, 10) : '';
};

const RevisitEditor = ({
  days,
  setDay,
  intervalUnit,
  setIntervalUnit,
  intervalAmount,
  setIntervalAmount,
  timeOfDay,
  setTimeOfDay,
  startsAt,
  setStartsAt,
  hideRecurrenceLabel = false,
  hideTimeOfDayInput,
  dayInMonth,
  setDayInMonth,
  weekInMonth,
  setWeekInMonth,
}: Props) => {
  const choosenDays = days?.split(' ') || [];

  const toggleDay = value => {
    let daysArray;
    if (choosenDays.includes(value)) {
      daysArray = choosenDays.length === 1 ? choosenDays : choosenDays.filter(choosenDay => choosenDay !== value);
    } else {
      daysArray = [...choosenDays, value];
    }
    setDay(daysArray.join(' '));
  };

  return (
    <div className="recurrence-editor">
      {!hideRecurrenceLabel && <label className="mt-3">Recurrence *</label>}
      <div className="d-flex align-items-center mb-2">
        <span className="start-date-label">Revisit on</span>
        <CustomSingleDatePicker date={startsAt} onDateChange={setStartsAt} withPortal />
      </div>
      <RecurrenceSettingsInput
        label="Repeat every"
        amountValue={intervalAmount}
        onAmountValueChange={e => setIntervalAmount(formatRecurrenceInput(e.target.value))}
        intervalValue={intervalUnit}
        intervalOptions={intervalUnitOptions(intervalAmount)}
        onIntervalValueChange={o => setIntervalUnit(o.value)}
      />

      {intervalUnit === 'week' ? (
        <div className="mt-2">
          Repeat on
          <DayPicker choosenDays={choosenDays} onDayClick={toggleDay} />
        </div>
      ) : null}

      {intervalUnit === 'month' ? (
        <div className="mt-2">
          <RecurrenceTimeInMonthInput
            label="on"
            startsAt={startsAt}
            dayInMonth={dayInMonth}
            weekInMonth={weekInMonth}
            onDayInMonthChange={setDayInMonth}
            onWeekInMonthChange={setWeekInMonth}
          />
        </div>
      ) : null}

      {!hideTimeOfDayInput && (
        <div className="d-flex align-items-center my-2 mt-3">
          <div className="mr-2 time-of-day-label">Repeat at</div>
          <TimeInput
            className="form-control time-of-day-input"
            value={timeOfDay}
            title="Repeat at"
            required
            onChange={value => setTimeOfDay(value || NEW_TIME_OF_DAY_DEFAULT)}
          />
        </div>
      )}
    </div>
  );
};

export default RevisitEditor;
